import { Outlet } from "react-router-dom";
import { Suspense, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/session";
import {
  Home,
  Calendar,
  Bell,
  Users,
  DollarSign,
  User,
  LogOut,
  Menu,
  Trophy
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { PageLoader } from "@/components/ui/page-loader";
import { cn } from "@/lib/utils";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";

export function ParentDashboardLayout() {
  const location = useLocation();
  const { user, logoutMutation } = useAuth();
  const shouldShowBanner = user && user.emailVerified === false;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const navigation = [
    { name: "My Kids", href: "/parent/kids", icon: Users, testId: "link-parent-kids" },
    { name: "Dashboard", href: "/dashboard/parent", icon: Home, testId: "link-parent-dashboard" },
    { name: "Schedule", href: "/parent/schedule", icon: Calendar, testId: "link-parent-schedule" },
    { name: "Games", href: "/parent/availability", icon: Trophy, testId: "link-parent-availability" },
    { name: "Announcements", href: "/parent/announcements", icon: Bell, testId: "link-parent-announcements" },
    { name: "Connect Child", href: "/parent/connect-child", icon: User, testId: "link-parent-connect" },
    { name: "Payments", href: "/parent/payments", icon: DollarSign, testId: "link-parent-payments" },
  ];
  
  const Logo = () => (
    <Link to="/parent/kids">
      <div className="flex items-center gap-2 font-bold text-xl text-primary cursor-pointer">
        <span className="bg-primary text-white p-1 rounded">
          🏏
        </span>
        <span>Legacy Cricket</span>
      </div>
    </Link>
  );

  const SidebarContent = () => (
    <>
      <div className="p-4 md:p-6">
        <Logo />
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 px-3 py-2 md:py-4 space-y-1" data-testid="parent-sidebar-nav">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          
          return (
            <Link 
              key={item.name} 
              to={item.href} 
              data-testid={item.testId}
              onClick={() => setSidebarOpen(false)}
            >
              <div
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive 
                    ? "bg-primary/10 text-primary hover:bg-primary/15" 
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{item.name}</span>
              </div>
            </Link>
          );
        })}
      </nav>
      
      {/* User Profile */}
      <div className="border-t p-4">
        {user && (
          <div className="mb-4">
            <Link to="/parent/profile" className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
              <Avatar className="h-8 w-8">
                {user.profileImage ? (
                  <AvatarImage src={user.profileImage} alt={user.fullName || "User"} />
                ) : null}
                <AvatarFallback>
                  {user.fullName ? user.fullName.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'P'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.fullName || 'Parent'}</p>
                <p className="text-xs text-gray-500">Parent Portal</p>
              </div>
            </Link>
          </div>
        )}
        <Button 
          variant="outline" 
          className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={() => logoutMutation.mutate()}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </>
  );
  
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50 overflow-x-hidden" data-testid="parent-layout">
      {/* Desktop Sidebar - Fixed on desktop, hidden on mobile */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:left-0 md:w-64 lg:w-72 md:border-r bg-white md:shadow-sm md:overflow-y-auto">
        <SidebarContent />
      </aside>

      {/* Mobile Drawer - Hidden on desktop */}
      {sidebarOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
          
          {/* Drawer */}
          <aside 
            className="fixed inset-y-0 left-0 w-64 bg-white z-50 flex flex-col md:hidden shadow-xl"
            data-testid="parent-sidebar"
          >
            <SidebarContent />
          </aside>
        </>
      )}
      
      {/* Main Content - Add left margin on desktop to account for fixed sidebar */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 md:ml-64 lg:ml-72">
        {/* Mobile Top Bar - Only visible on mobile */}
        <header className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-md"
            data-testid="parent-mobile-menu-button"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5 text-gray-700" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <span className="bg-primary text-white p-1 rounded text-sm">
              🏏
            </span>
            <span className="font-semibold text-gray-900">Parent Portal</span>
          </div>
        </header>

        {/* Desktop Header - Only visible on desktop */}
        <header className="hidden md:block bg-white border-b border-gray-200 px-6 py-4">
          <h1 className="text-xl font-semibold text-gray-900">Parent Portal</h1>
        </header>
        
        {/* Email Verification Banner */}
        {shouldShowBanner && <VerifyEmailBanner />}
        
        {/* Page Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
